import StoreTags from '@enums/StoreTags'
import { CommonQueryParams, ItemsFromResponse } from '@models/common'
import {
  CreateEmployeeEducationBody,
  EmployeeEducation,
  EmployeeEducationDetailed,
  EmployeeEducationResponse,
  UpdateEmployeeEducationBody,
} from '@models/employeeEducation'
import {
  ANTI_FIRE_REPORT,
  ELECTRO_SAFE_REPORT,
  INITIAL_BRIEFING_REPORT,
} from '@const/reportNames'
import { createDownloadReportQuery } from '@utils/reports'
import { HYDRA_MEMBER, HYDRA_TOTAL_ITEMS } from '../../config/consts'
import emptySplitApi from '../api'

const BASE_URL = '/api/education_destinations'

export const employeeEducationApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getEmployeeEducations: builder.query<
      { items: EmployeeEducation[]; length: number },
      CommonQueryParams
    >({
      query: (params) => ({
        url: BASE_URL,
        params,
      }),
      transformResponse: (
        response: ItemsFromResponse<EmployeeEducationResponse>
      ) => ({
        items: response[HYDRA_MEMBER].map((item) => ({
          ...item,
          value: item['@id'],
          label: item.title,
        })),
        length: response[HYDRA_TOTAL_ITEMS],
      }),
      providesTags: [StoreTags.EMPLOYEE_EDUCATIONS],
    }),
    createEmployeeEducation: builder.mutation<
      EmployeeEducationDetailed,
      CreateEmployeeEducationBody
    >({
      invalidatesTags: (result) =>
        result ? [StoreTags.EMPLOYEE_EDUCATIONS] : [],
      query: (body) => ({
        url: BASE_URL,
        method: 'POST',
        body,
      }),
    }),
    updateEmployeeEducation: builder.mutation<
      EmployeeEducationDetailed,
      UpdateEmployeeEducationBody
    >({
      invalidatesTags: (result) =>
        result
          ? [StoreTags.EMPLOYEE_EDUCATIONS, StoreTags.EMPLOYEE_EDUCATION]
          : [],
      query: ({ id, ...body }) => ({
        url: `${BASE_URL}/${id}`,
        method: 'PUT',
        body,
      }),
    }),
    getEmployeeEducation: builder.query<EmployeeEducationDetailed, number>({
      providesTags: [StoreTags.EMPLOYEE_EDUCATION],
      query: (id) => `${BASE_URL}/${id}`,
    }),
    deleteEmployeeEducation: builder.mutation<void, number>({
      invalidatesTags: [StoreTags.EMPLOYEE_EDUCATIONS],
      query: (id) => ({
        url: `${BASE_URL}/${id}`,
        method: 'DELETE',
      }),
    }),
    getEducationProtocolReport: builder.mutation<void, string | number>({
      query: (id) =>
        createDownloadReportQuery({
          url: `${BASE_URL}/${id}/protocol_report`,
          method: 'POST',
        })(),
      invalidatesTags: [StoreTags.REPORTS],
    }),
    getMinistryLaborReportXml: builder.mutation<void, string | number>({
      query: (id) =>
        createDownloadReportQuery({
          url: `${BASE_URL}/${id}/ministry_labor_report_xml`,
          method: 'POST',
        })(),
      invalidatesTags: [StoreTags.REPORTS],
    }),
    getMinistryLaborReportXlsx: builder.mutation<void, string | number>({
      query: (id) =>
        createDownloadReportQuery({
          url: `${BASE_URL}/${id}/ministry_labor_report_xlsx`,
          method: 'POST',
        })(),
      invalidatesTags: [StoreTags.REPORTS],
    }),
    getElectricalSafetyReport: builder.mutation<void, CommonQueryParams>({
      query: createDownloadReportQuery({
        url: `${BASE_URL}/${ELECTRO_SAFE_REPORT}`,
        method: 'POST',
      }),
      invalidatesTags: [StoreTags.REPORTS],
    }),
    getFireSafetyReport: builder.mutation<void, CommonQueryParams>({
      query: createDownloadReportQuery({
        url: `${BASE_URL}/${ANTI_FIRE_REPORT}`,
        method: 'POST',
      }),
      invalidatesTags: [StoreTags.REPORTS],
    }),
    getEmployeesInitialBriefingReport: builder.mutation<
      void,
      CommonQueryParams
    >({
      query: createDownloadReportQuery({
        url: `${BASE_URL}/${INITIAL_BRIEFING_REPORT}`,
        method: 'POST',
      }),
      invalidatesTags: [StoreTags.REPORTS],
    }),
    getEmployeesOtherBriefingReport: builder.mutation<void, CommonQueryParams>({
      query: createDownloadReportQuery({
        url: `${BASE_URL}/multiple_briefing_report`,
        method: 'POST',
      }),
      invalidatesTags: [StoreTags.REPORTS],
    }),
  }),
})

export const {
  useGetEmployeeEducationsQuery,
  useCreateEmployeeEducationMutation,
  useUpdateEmployeeEducationMutation,
  useGetEmployeeEducationQuery,
  useDeleteEmployeeEducationMutation,
  useGetEducationProtocolReportMutation,
  useGetMinistryLaborReportXmlMutation,
  useGetMinistryLaborReportXlsxMutation,
  useGetElectricalSafetyReportMutation,
  useGetFireSafetyReportMutation,
  useGetEmployeesInitialBriefingReportMutation,
  useGetEmployeesOtherBriefingReportMutation,
} = employeeEducationApi
